import React, { useState, useEffect } from 'react';
import { getColours, getFieldCollections, createFieldCollection } from '../../lib/treeApi';
import { useTreeAuth } from '../../lib/treeAuth';
import TreeTaskLayout from '../../components/trees/TreeTaskLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Minus, TreePine, Check, Clock, Truck } from 'lucide-react';
import { toast } from 'sonner';

import { swatchStyle, registerColours, colourHex } from '../../lib/treeColors';

export default function TreeFieldCollectorTask() {
  const { settings } = useTreeAuth();
  const FULL_LOAD = settings.pallets_per_trailer;
  const [colours, setColours] = useState([]);
  const [collections, setCollections] = useState([]);
  const [counts, setCounts] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [collectOpen, setCollectOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [colRes, fcRes] = await Promise.all([getColours(), getFieldCollections()]);
      setColours(colRes.data);
      registerColours(colRes.data);
      setCollections(fcRes.data);
      const init = {};
      colRes.data.forEach(c => { init[c.color] = 0; });
      setCounts(init);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const adjust = (color, delta) => setCounts(prev => ({ ...prev, [color]: Math.max(0, (prev[color] || 0) + delta) }));

  const totalPallets = Object.values(counts).reduce((s, v) => s + v, 0);

  const handleSubmit = async () => {
    if (totalPallets === 0) return toast.error('Add at least 1 pallet');
    setSubmitting(true);
    try {
      const items = Object.entries(counts).filter(([_, q]) => q > 0).map(([color, quantity]) => ({ color, quantity }));
      await createFieldCollection({ items, notes });
      toast.success(`${totalPallets} pallet${totalPallets === 1 ? '' : 's'} confirmed to yard`);
      setNotes('');
      setCounts(Object.fromEntries(colours.map(c => [c.color, 0])));
      setCollectOpen(false);
      fetchData();
    } catch { toast.error('Failed to save'); }
    setSubmitting(false);
  };

  const fmtDate = (s) => {
    const d = new Date(s);
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <TreeTaskLayout title="Field Collection" subtitle="Confirm pallets returning to the yard">
      <div className="space-y-4" data-testid="tree-field-collector-task">
        <Button
          onClick={() => setCollectOpen(true)}
          className="w-full h-20 bg-[#1A4331] text-white text-xl font-bold rounded-2xl flex items-center justify-center gap-3 active:scale-[0.98] transition-transform"
          data-testid="tree-open-collect-modal"
        >
          <Truck className="w-7 h-7" /> Collect From Field to Yard
        </Button>

        <Dialog open={collectOpen} onOpenChange={setCollectOpen}>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="tree-collect-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Collect From Field to Yard</DialogTitle>
              <p className="text-xs text-[#4A5D53]">Tap + for each colour, then confirm</p>
            </DialogHeader>

            <div className="space-y-2" data-testid="tree-task-collector-list">
              {colours.map((c) => (
                <div key={c.color} className="bg-[#F8F6F3] rounded-xl p-2.5 flex items-center gap-3" data-testid={`tree-task-collect-${c.color}`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={swatchStyle(c.color)}>
                    <TreePine className="w-4 h-4 text-white drop-shadow" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#0A1711] font-['Outfit'] capitalize text-sm">{c.label || c.color}</p>
                    <p className="text-[10px] text-[#4A5D53]">{c.quantity} in yard</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => adjust(c.color, -1)}
                      className="w-10 h-10 rounded-lg bg-red-50 border-2 border-red-200 flex items-center justify-center text-red-600 active:scale-95 transition-transform"
                      data-testid={`tree-task-collect-minus-${c.color}`}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <div className="w-9 text-center">
                      <p className="text-xl font-bold text-[#0A1711] font-['Outfit'] tabular-nums">{counts[c.color] || 0}</p>
                    </div>
                    <button
                      onClick={() => adjust(c.color, 1)}
                      className="w-10 h-10 rounded-lg bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center text-emerald-600 active:scale-95 transition-transform"
                      data-testid={`tree-task-collect-plus-${c.color}`}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {colours.length === 0 && !loading && (
                <p className="text-center text-sm text-[#4A5D53] py-8">No colours set up yet. Ask an admin to add one.</p>
              )}
            </div>

            <div className={`flex items-center justify-between rounded-lg px-3 py-2 transition-colors ${totalPallets >= FULL_LOAD ? 'bg-emerald-50 border border-emerald-300' : 'bg-[#1A4331]/5'}`} data-testid="tree-task-collect-total">
              <span className={`text-xs font-semibold ${totalPallets >= FULL_LOAD ? 'text-emerald-700' : 'text-[#4A5D53]'}`}>
                {totalPallets >= FULL_LOAD ? `Full load! (${FULL_LOAD} pallets)` : `Total this trip (full load = ${FULL_LOAD})`}
              </span>
              <span className={`text-lg font-bold font-['Outfit'] ${totalPallets >= FULL_LOAD ? 'text-emerald-700' : 'text-[#1A4331]'}`}>{totalPallets} pallets</span>
            </div>

            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" className="bg-white h-12" data-testid="tree-task-notes-input" />

            <DialogFooter>
              <Button variant="outline" onClick={() => setCollectOpen(false)} data-testid="tree-task-collect-cancel">Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="bg-[#1A4331] text-white h-12" data-testid="tree-task-submit-button">
                <Check className="w-5 h-5 mr-2" /> Confirm to Yard
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div>
          <h2 className="text-sm font-bold text-[#4A5D53] uppercase tracking-wider mb-2">Recent</h2>
          <div className="space-y-2" data-testid="tree-task-history">
            {collections.length === 0 ? (
              <p className="text-center text-sm text-[#4A5D53] py-6">No collections recorded yet</p>
            ) : (
              collections.slice(0, 8).map((fc) => (
                <div key={fc.id} className="bg-white border border-[#1A4331]/15 rounded-xl px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-[10px] text-[#4A5D53] mb-1"><Clock className="w-3 h-3" />{fmtDate(fc.created_at)}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {fc.items?.map((item, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-xs font-semibold" style={{ backgroundColor: `${colourHex(item.color)}15`, color: colourHex(item.color) }}>
                        <div className="w-2 h-2 rounded-full" style={swatchStyle(item.color)} />
                        {item.quantity}x {item.color}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </TreeTaskLayout>
  );
}
